/**
 * Price input only for Consultant Contracts
 * compensation (ersättning) - currency (valuta) - type (ersättningstyp)
 * currency - there should be only one value on the form but could be more than one field
 */

import React from "react";
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  makeStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Select, Text } from "@common/formx_item_form";
import { ThemeType } from "@theme";
import {
  ConsultantContractTravelsPeriod,
  ConsultantContractSalaryType,
  LegalPersonType
} from "@graphql";
import { Remove } from "@material-ui/icons";
import {
  calculatePriceOverSalaryLimit,
  calculatePriceUnderSalaryLimit,
  introductionBlank
} from "@consultants/ConsultantContract/utils";

type Props = {
  name: string;
  error?: string;
  type?: "travels" | "accommodation";
  contractVariant?: LegalPersonType;
  options?: { label: string; value: string }[];
};

type IntroductionProps = {
  name: string;
  form: any;
  add: (name: string, value) => void;
  remove: (name: string, index: number) => void;
  error?: string;
  isNationalProcurementPriceStrategy?: boolean;
};

export const IntroductionPriceInput: React.FC<IntroductionProps> = ({
  name,
  error,
  form,
  add,
  remove,
  isNationalProcurementPriceStrategy
}) => {
  const classes = useStyles({});
  return (
    <FormControl error={!!error} fullWidth key={"IntroductionPrice"}>
      <div className={classes.bonusWrap}>
        <FormLabel component="legend">{locale.Introduction}</FormLabel>
        <Button onClick={() => add(name, introductionBlank)}>
          {locale.AddIntroduction}
        </Button>
      </div>
      {form.getValue(name)?.map((x, i) => (
        <Grid key={i} item xs={12} container spacing={1}>
          <Grid item xs={6}>
            <Text
              name={`${name}[${i}].Description`}
              label={locale.Description}
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Text
              name={`${name}[${i}].Sum`}
              label={locale.Sum}
              TextFieldProps={{
                type: "number"
              }}
            />
          </Grid>
          {isNationalProcurementPriceStrategy && (
            <Grid item xs={1} style={{ minWidth: "180px" }}>
              <div>
                {locale.Sum + " <7,5: "}
                <b>
                  {calculatePriceUnderSalaryLimit(
                    form.getValue(`${name}[${i}].Sum`),
                    "kr"
                  )}
                </b>
              </div>
              <div>
                {locale.Sum + " >7,5: "}
                <b>
                  {calculatePriceOverSalaryLimit(
                    form.getValue(`${name}[${i}].Sum`),
                    "kr"
                  )}
                </b>
              </div>
            </Grid>
          )}
          <Grid item>
            <IconButton aria-label="delete" onClick={() => remove(name, i)}>
              <Remove fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </FormControl>
  );
};

export const salaryTypeOption = [
  {
    label: locale.consultantContractSalaryType[ConsultantContractSalaryType.GrossSalary],
    value: ConsultantContractSalaryType.GrossSalary
  }
];

export const defaultSalaryTypeOpt = Object.values(ConsultantContractSalaryType).map(
  (v) => ({
    label: locale.consultantContractSalaryType[v],
    value: v
  })
);

const ConsultantContractTravelsPeriodOpt = [
  ConsultantContractTravelsPeriod.Week,
  ConsultantContractTravelsPeriod.WorkShift,
  ConsultantContractTravelsPeriod.PerTravel
];

const ConsultantContractPeriodOpt = [
  ConsultantContractTravelsPeriod.Week,
  ConsultantContractTravelsPeriod.WorkShift
];

export const MaxCost: React.FC<Props> = ({
  name,
  error,
  type,
  contractVariant,
  options = defaultSalaryTypeOpt
}) => {
  const classes = useStyles({});
  const opt = {
    value:
      type === "travels"
        ? ConsultantContractTravelsPeriodOpt
        : ConsultantContractPeriodOpt,
    label:
      type === "travels" ? "consultantContractTravelsPeriod" : "consultantContractPeriod"
  };
  return (
    <FormControl error={!!error} fullWidth>
      <FormGroup row className={classes.wrap}>
        <Text
          name={`${name}.Price`}
          label={locale.MaxCost}
          TextFieldProps={{
            type: "number",
            error: !!error
          }}
        />
        <Select
          clearable={false}
          label={locale.CompensationType}
          name={`${name}.Period`}
          error={!!error}
          options={Object.values(opt.value).map((v) => ({
            label: locale[opt.label][v],
            value: v
          }))}
        />
        {contractVariant === LegalPersonType.Employee && (
          <Select
            clearable={false}
            label={locale.SalaryType}
            name={`${name}.SalaryType`}
            error={!!error}
            options={options}
          />
        )}
      </FormGroup>
    </FormControl>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  wrap: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "flex-end"
  },
  bonusWrap: {
    padding: "10px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }
}));
